import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";

import { getUserProductItemData, removeProduct } from "../API/firebase";
import { describeFirebaseError } from "../lib/firebase-errors";
import { ProductItemTypes } from "../models/types";
import { Eyebrow, PillButton, SurfaceCard } from "../components/ui";
import GuideLink from "../components/guides/GuideLink";
import { paths } from "./paths";
import { tokens } from "../styles/tokens";

function DetailRow({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 1.25 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="subtitle2" sx={{ textAlign: "right" }}>
          {value}
        </Typography>
      </Box>
      <Divider />
    </>
  );
}

const UserProductItem: React.FC = () => {
  const navigate = useNavigate();
  // Reads its own route param. This previously read a store whose setter had no
  // call sites and whose default was a hardcoded test serial, so the page always
  // showed the same placeholder device.
  const { artomeSerialNo = "" } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [product, setProduct] = useState<ProductItemTypes | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    setError(null);

    getUserProductItemData(artomeSerialNo)
      .then((data) => {
        if (!aborted) setProduct(data);
      })
      .catch(() => {
        if (!aborted) setError("This device could not be found on your account.");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });

    return () => {
      aborted = true;
    };
  }, [artomeSerialNo]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await removeProduct(artomeSerialNo);
      navigate(paths.accountDevices);
    } catch (err) {
      console.error("Device removal failed", err);
      setError(describeFirebaseError(err, "The removal"));
      setDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        width: `min(100%, 760px)`,
        mx: "auto",
        px: { mobile: 2.5, tablet: 4 },
        py: { mobile: 4, laptop: 6 },
      }}
    >
      <Eyebrow>Registered device</Eyebrow>
      <Typography variant="h2" sx={{ mb: 3 }}>
        {product?.artome_product ?? "Device"}
      </Typography>

      {loading ? (
        <SurfaceCard>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        </SurfaceCard>
      ) : error ? (
        <>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <PillButton tone="outline" onClick={() => navigate(paths.accountDevices)}>
            Back to devices
          </PillButton>
        </>
      ) : (
        product && (
          <SurfaceCard>
            <Box sx={{ mb: 2 }}>
              <DetailRow label="Product" value={product.artome_product} />
              <DetailRow label="Artome serial" value={product.artome_serial_no} />
              <DetailRow label="Projector serial" value={product.epson_serial_no} />
              <DetailRow label="Owner" value={product.product_owner} />
              <DetailRow label="Registered" value={product.setup_date} />
              <DetailRow label="Last updated" value={product.re_setup_date} />
              <DetailRow label="Lens serial" value={product.lens} />
            </Box>

            {product.previous_owners && product.previous_owners.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Previous owners
                </Typography>
                {product.previous_owners.map((entry, index) => (
                  <Typography
                    key={`${entry.owner}-${index}`}
                    variant="body2"
                    color="text.secondary"
                  >
                    {entry.owner}
                  </Typography>
                ))}
              </Box>
            )}

            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                bgcolor: "background.default",
                border: `1px solid ${tokens.color.hairline}`,
              }}
            >
              <GuideLink product={product.artome_product} variant="block" />
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <PillButton
                tone="outline"
                onClick={() => navigate(paths.accountDevices)}
              >
                Back to devices
              </PillButton>
              <Button color="error" onClick={() => setDialogOpen(true)}>
                Remove device
              </Button>
            </Box>
          </SurfaceCard>
        )
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Remove this device?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the registration record for {artomeSerialNo}. It cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleting} autoFocus>
            {deleting ? <CircularProgress size={20} color="inherit" /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProductItem;
